import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import ConfirmDialog from "../../components/ConfirmDialog";
import MediaRenderer from "../../components/MediaRenderer";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { uploadAdminMedia } from "../../lib/mediaUpload";
import { DEFAULT_NEWS_ITEMS } from "../../lib/newsData";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { mergeSiteContent } from "../../utils/helpers";

function emptyNews() {
  return {
    title: "",
    description: "",
    content: "",
    category: "News",
    media: "",
    mediaType: "image",
    externalUrl: "",
    buttonLabel: "Découvrir",
    visible: true,
    date: new Date().toISOString().slice(0, 10),
  };
}

function Field({ label, value, onChange, textarea = false, type = "text" }) {
  const Input = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">{label}</span>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full rounded border border-white/10 bg-zinc-950 px-3 py-2 text-white outline-none focus:border-red-500 ${
          textarea ? "min-h-[96px]" : ""
        }`}
      />
    </label>
  );
}

function setIn(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cursor = clone;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return clone;
}

function normalizeNewsItem(item) {
  return {
    ...item,
    title: item.title || "",
    category: item.category || "News",
    description: item.description || item.excerpt || "",
    content: item.content || item.fullText || item.description || item.excerpt || "",
    media: item.media || item.image || "",
    mediaType: item.mediaType || "image",
    externalUrl: item.externalUrl || item.href || "",
    buttonLabel: item.buttonLabel || "Découvrir",
    visible: item.visible !== false,
    date: item.date || "",
  };
}

function newsPayload(item, includeCreatedAt = false) {
  return sanitizeForFirestore({
    ...item,
    excerpt: item.description || "",
    fullText: item.content || item.description || "",
    image: item.media || "",
    href: item.externalUrl || "",
    ...(includeCreatedAt ? { createdAt: serverTimestamp() } : {}),
    updatedAt: serverTimestamp(),
  });
}

function NewsEditor({ item, onSave, onDelete, newItem = false }) {
  const [draft, setDraft] = useState(item);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => setDraft(item), [item]);

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const media = await uploadAdminMedia(file, "site/news");
      setDraft((current) => ({
        ...current,
        media,
        mediaType: file.type.startsWith("video/") ? "video" : "image",
      }));
      toast.success("Média prêt", "Sauvegarde la news pour publier la modification.");
    } catch (err) {
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-lg border border-white/10 bg-black p-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[180px_1fr]">
        <MediaRenderer
          src={draft.media}
          type={draft.mediaType}
          alt=""
          background
          className="aspect-[4/3] h-full w-full rounded object-cover"
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Titre" value={draft.title} onChange={(v) => setDraft((d) => ({ ...d, title: v }))} />
          <Field label="Catégorie" value={draft.category} onChange={(v) => setDraft((d) => ({ ...d, category: v }))} />
          <div className="md:col-span-2">
            <Field textarea label="Description courte" value={draft.description} onChange={(v) => setDraft((d) => ({ ...d, description: v }))} />
          </div>
          <div className="md:col-span-2">
            <Field textarea label="Article complet" value={draft.content} onChange={(v) => setDraft((d) => ({ ...d, content: v }))} />
          </div>
          <Field label="URL média" value={draft.media} onChange={(v) => setDraft((d) => ({ ...d, media: v }))} />
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">Type média</span>
            <select
              value={draft.mediaType || "image"}
              onChange={(event) => setDraft((d) => ({ ...d, mediaType: event.target.value }))}
              className="w-full rounded border border-white/10 bg-zinc-950 px-3 py-2 text-white outline-none focus:border-red-500"
            >
              <option value="image">Image</option>
              <option value="video">Vidéo</option>
            </select>
          </label>
          <Field label="Lien externe ou interne" value={draft.externalUrl} onChange={(v) => setDraft((d) => ({ ...d, externalUrl: v }))} />
          <Field label="Texte bouton" value={draft.buttonLabel} onChange={(v) => setDraft((d) => ({ ...d, buttonLabel: v }))} />
          <Field label="Date" type="date" value={draft.date} onChange={(v) => setDraft((d) => ({ ...d, date: v }))} />
          <label className="flex items-center gap-2 self-end rounded border border-white/10 px-3 py-2 text-sm font-bold uppercase">
            <input
              type="checkbox"
              checked={draft.visible !== false}
              onChange={(event) => setDraft((d) => ({ ...d, visible: event.target.checked }))}
            />
            Visible
          </label>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <label className={`cursor-pointer rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
          {uploading ? "Upload..." : "Importer image ou vidéo"}
          <input
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              uploadMedia(file);
            }}
          />
        </label>
        <button
          onClick={save}
          disabled={saving || uploading}
          className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-60"
        >
          {saving ? "Sauvegarde..." : newItem ? "Ajouter la news" : "Sauvegarder"}
        </button>
        {!newItem && onDelete && (
          <button onClick={() => onDelete(draft)} className="rounded-full bg-red-600 px-4 py-2 text-xs font-bold uppercase">
            Supprimer
          </button>
        )}
      </div>
    </article>
  );
}

function PageNewsEditor({ item, onSave }) {
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(item), [item]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Page publique</p>
      <h2 className="mb-5 mt-2 text-2xl font-black uppercase">Titres de la page News</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Libellé du haut" value={draft.eyebrow} onChange={(v) => setDraft((d) => ({ ...d, eyebrow: v }))} />
        <Field label="Titre principal" value={draft.title} onChange={(v) => setDraft((d) => ({ ...d, title: v }))} />
        <Field label="Libellé news mise en avant" value={draft.featuredEyebrow} onChange={(v) => setDraft((d) => ({ ...d, featuredEyebrow: v }))} />
        <Field label="Bouton toutes les news" value={draft.allNewsLabel} onChange={(v) => setDraft((d) => ({ ...d, allNewsLabel: v }))} />
        <Field label="Libellé du journal" value={draft.journalEyebrow} onChange={(v) => setDraft((d) => ({ ...d, journalEyebrow: v }))} />
        <Field label="Titre du journal" value={draft.journalTitle} onChange={(v) => setDraft((d) => ({ ...d, journalTitle: v }))} />
      </div>
      <button onClick={save} disabled={saving} className="mt-4 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-60">
        {saving ? "Sauvegarde..." : "Sauvegarder les titres"}
      </button>
    </section>
  );
}

function FeaturedNewsEditor({ item, onSave }) {
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const toast = useToast();

  useEffect(() => setDraft(item), [item]);

  const upload = async (file, target) => {
    if (!file) return;
    setUploading(target);
    try {
      const url = await uploadAdminMedia(file, "site/home/news");
      setDraft((current) => ({
        ...current,
        [target]: url,
        ...(target === "video" ? { mediaType: file.type.startsWith("video/") ? "video" : "image" } : {}),
      }));
      toast.success("Média prêt", "Sauvegarde la news mise en avant pour publier la modification.");
    } catch (err) {
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">À la une</p>
      <h2 className="mb-5 mt-2 text-2xl font-black uppercase">News principale</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
        <MediaRenderer
          src={draft.video}
          type={draft.mediaType || "video"}
          fallback={draft.fallbackImage}
          alt={draft.title}
          background
          controls
          className="aspect-video h-full w-full rounded object-cover"
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Titre" value={draft.title} onChange={(v) => setDraft((d) => ({ ...d, title: v }))} />
          </div>
          <div className="md:col-span-2">
            <Field textarea label="Sous-titre" value={draft.subtitle} onChange={(v) => setDraft((d) => ({ ...d, subtitle: v }))} />
          </div>
          <Field label="URL média de fond" value={draft.video} onChange={(v) => setDraft((d) => ({ ...d, video: v }))} />
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">Type média</span>
            <select
              value={draft.mediaType || "video"}
              onChange={(event) => setDraft((d) => ({ ...d, mediaType: event.target.value }))}
              className="w-full rounded border border-white/10 bg-zinc-950 px-3 py-2 text-white outline-none focus:border-red-500"
            >
              <option value="video">Vidéo</option>
              <option value="image">Image</option>
            </select>
          </label>
          <Field label="Image fallback" value={draft.fallbackImage} onChange={(v) => setDraft((d) => ({ ...d, fallbackImage: v }))} />
          <Field label="Lien Regarder" value={draft.watchUrl} onChange={(v) => setDraft((d) => ({ ...d, watchUrl: v }))} />
          <Field label="Texte Regarder" value={draft.watchLabel} onChange={(v) => setDraft((d) => ({ ...d, watchLabel: v }))} />
          <Field label="Lien Découvrir" value={draft.discoverUrl} onChange={(v) => setDraft((d) => ({ ...d, discoverUrl: v }))} />
          <Field label="Texte Découvrir" value={draft.discoverLabel} onChange={(v) => setDraft((d) => ({ ...d, discoverLabel: v }))} />
          <label className="flex items-center gap-2 self-end rounded border border-white/10 px-3 py-2 text-sm font-bold uppercase">
            <input
              type="checkbox"
              checked={draft.enabled !== false}
              onChange={(event) => setDraft((d) => ({ ...d, enabled: event.target.checked }))}
            />
            Afficher la news principale
          </label>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <label className={`cursor-pointer rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
          {uploading === "video" ? "Upload..." : "Importer le média de fond"}
          <input type="file" accept="image/*,video/*" className="hidden" onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            upload(file, "video");
          }} />
        </label>
        <label className={`cursor-pointer rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
          {uploading === "fallbackImage" ? "Upload..." : "Importer l'image fallback"}
          <input type="file" accept="image/*" className="hidden" onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            upload(file, "fallbackImage");
          }} />
        </label>
        <button onClick={save} disabled={saving || Boolean(uploading)} className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-60">
          {saving ? "Sauvegarde..." : "Sauvegarder la news principale"}
        </button>
      </div>
    </section>
  );
}

export default function AdminNews() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyNews());
  const [pendingDelete, setPendingDelete] = useState(null);
  const [content, setContent] = useState(() => mergeSiteContent());
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(collection(db, "news"), (snap) => {
      setItems(snap.docs.map((item) => normalizeNewsItem({ id: item.id, ...item.data() })));
    });
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, "admin", "content"), (snap) => {
      setContent(mergeSiteContent(snap.exists() ? snap.data() : {}));
    });
  }, [isAdmin]);

  const saveContentSection = async (path, value, successTitle) => {
    const next = setIn(content, path, value);
    setContent(next);
    try {
      await setDoc(doc(db, "admin", "content"), sanitizeForFirestore(next), { merge: true });
      toast.success(successTitle);
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  const addNews = async (item) => {
    if (!item.title.trim()) {
      toast.error("Titre requis");
      return;
    }
    try {
      await addDoc(collection(db, "news"), newsPayload(item, true));
      setDraft(emptyNews());
      toast.success("News ajoutée");
    } catch (err) {
      toast.error("Ajout impossible", err.message);
    }
  };

  const saveNews = async (item) => {
    try {
      await updateDoc(doc(db, "news", item.id), newsPayload(item));
      toast.success("News sauvegardée");
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  const importDefaultNews = async (successTitle = "News actuelles importées dans l'admin") => {
    try {
      await Promise.all(DEFAULT_NEWS_ITEMS.map((item) =>
        setDoc(doc(db, "news", item.id), newsPayload(item, true), { merge: true })
      ));
      await saveContentSection("pages.news.useDefaultItems", false, successTitle);
      return true;
    } catch (err) {
      toast.error("Import impossible", err.message);
      return false;
    }
  };

  const saveDefaultNews = async (item) => {
    const imported = await importDefaultNews("Cartes News rendues modifiables");
    if (!imported) return;
    try {
      await setDoc(doc(db, "news", item.id), newsPayload(item), { merge: true });
      toast.success("News sauvegardée");
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  const removeNews = async () => {
    if (!pendingDelete) return;
    try {
      await deleteDoc(doc(db, "news", pendingDelete.id));
      await saveContentSection("pages.news.useDefaultItems", false, "News supprimée");
      setPendingDelete(null);
    } catch (err) {
      toast.error("Suppression impossible", err.message);
    }
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin News</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">Changer de compte</button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">Connexion Google</button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin</p>
            <h1 className="mt-2 text-4xl font-black uppercase">News</h1>
            <p className="mt-2 text-sm text-gray-400">Ajoute et publie les actualités du site.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/news" className="rounded-full bg-white px-4 py-2 text-sm font-bold uppercase text-black">Voir la page</Link>
            <Link to="/admin" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Dashboard</Link>
          </div>
        </header>

        <PageNewsEditor
          item={content.pages.news}
          onSave={(value) => saveContentSection("pages.news", value, "Titres de la page News sauvegardés")}
        />

        <FeaturedNewsEditor
          item={content.pages.home.newsHero}
          onSave={(value) => saveContentSection("pages.home.newsHero", value, "News principale sauvegardée")}
        />

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Nouvelle entrée</p>
          <h2 className="mb-5 mt-2 text-2xl font-black uppercase">Ajouter une news</h2>
          <NewsEditor item={draft} onSave={addNews} newItem />
        </section>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Bibliothèque</p>
          <h2 className="mb-5 mt-2 text-2xl font-black uppercase">News publiées</h2>
          {items.length === 0 && content.pages.news.useDefaultItems !== false && (
            <div className="mb-5 rounded-lg border border-red-500/30 bg-red-950/20 p-4">
              <p className="font-bold">Les deux cartes actuellement visibles utilisent encore le contenu initial du site.</p>
              <p className="mt-1 text-sm text-gray-300">Importe-les dans l'admin pour les modifier, les masquer ou les supprimer individuellement.</p>
              <button onClick={() => importDefaultNews()} className="mt-4 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black">
                Rendre les cartes modifiables
              </button>
            </div>
          )}
          <div className="space-y-4">
            {items.length === 0 && content.pages.news.useDefaultItems === false && <p className="text-gray-500">Aucune news enregistrée.</p>}
            {(items.length ? items : content.pages.news.useDefaultItems !== false ? DEFAULT_NEWS_ITEMS : []).map((item) => (
              <NewsEditor
                key={item.id}
                item={item}
                onSave={items.length ? saveNews : saveDefaultNews}
                onDelete={items.length ? setPendingDelete : null}
              />
            ))}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Supprimer cette news ?"
        message="Es-tu sûr de vouloir supprimer cette news ? Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={removeNews}
        onCancel={() => setPendingDelete(null)}
      />
    </main>
  );
}
